import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { getListing, getListings, CATEGORY_LABELS } from "../lib/api";

export default function ListingDetailPage() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setListing(null);
    getListing(slug)
      .then(d => {
        setListing(d);
        window.scrollTo({ top: 0, behavior: "instant" });
        return getListings({ category: d.category });
      })
      .then(items => setRelated(items.filter(i => i.slug !== slug).slice(0, 3)))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <Navigate to="/" replace />;
  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-serif-ml italic text-[var(--ml-text-dim)] text-2xl">Loading the issue…</div>
      </div>
    );
  }

  return (
    <div className="grain" data-testid={`listing-detail-${listing.slug}`}>
      {/* Hero */}
      <section className="relative h-screen min-h-[700px] w-full overflow-hidden">
        <div className="absolute inset-0 kenburns">
          <img src={listing.hero_image} alt={listing.name} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-[var(--ml-bg)]" />
        <div className="absolute inset-0 vignette" />

        <div className="relative z-10 container-ml min-h-screen flex flex-col">
          <div className="pt-32 md:pt-36">
            <Link to={`/category/${listing.category}`} data-testid="back-to-category" className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.28em] uppercase text-[var(--ml-text-dim)] hover:text-[var(--ml-gold)] transition-colors">
              <ArrowLeft size={14} /> {CATEGORY_LABELS[listing.category]}
            </Link>
          </div>

          <div className="flex-1 flex items-end pb-20 md:pb-28">
            <div className="reveal-up max-w-5xl">
              <p className="font-mono-ml text-[0.65rem] tracking-[0.3em] uppercase text-[var(--ml-gold)] mb-6">
                {CATEGORY_LABELS[listing.category]}
              </p>
              <h1 className="font-serif-ml text-6xl sm:text-7xl md:text-[9rem] leading-[0.85] tracking-[-0.02em] text-[var(--ml-text)] mb-8">
                {listing.name}
              </h1>
              <p className="font-serif-ml italic text-2xl md:text-3xl text-[var(--ml-text-dim)] max-w-3xl leading-snug">
                {listing.tagline}
              </p>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-8 text-[0.7rem] tracking-[0.22em] uppercase text-[var(--ml-text-dim)]">
                <span className="flex items-center gap-2"><MapPin size={14} className="text-[var(--ml-gold)]" />{listing.location}</span>
                {listing.price_range && <span className="text-[var(--ml-gold)]">{listing.price_range}</span>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container-ml py-24 md:py-32">
        <div className="grid grid-cols-12 gap-6 md:gap-16">
          {/* Side meta */}
          <aside className="col-span-12 md:col-span-3 md:sticky md:top-32 self-start">
            <p className="overline mb-4">The Details</p>
            <ul className="space-y-3 text-sm text-[var(--ml-text-dim)] mb-10">
              {listing.tags?.map(t => (
                <li key={t} className="border-b border-white/5 pb-3">{t}</li>
              ))}
            </ul>
            {listing.reservation_url ? (
              <a
                href="https://booking.travelchannelworld.com/en/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="listing-reserve-btn"
                className="btn-ml-gold inline-block w-full text-center"
              >
                Reserve now
              </a>
            ) : (
              <Link to="/concierge" data-testid="listing-inquire-btn" className="btn-ml-gold inline-block w-full text-center">
                Inquire to reserve
              </Link>
            )}
            <p className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--ml-text-muted)] mt-4 text-center">
              {listing.reservation_url ? "Secure booking · external" : "Reservation opening soon"}
            </p>
          </aside>

          {/* Editorial body */}
          <div className="col-span-12 md:col-span-9 max-w-3xl">
            <p className="overline mb-6">An essay</p>
            <p className="font-serif-ml text-3xl md:text-4xl italic leading-[1.2] text-[var(--ml-text)] mb-12 border-l-2 border-[var(--ml-gold)] pl-6">
              "{listing.excerpt}"
            </p>
            {listing.body?.map((p, i) => (
              <p
                key={i}
                className={`text-[var(--ml-text)] text-lg md:text-xl leading-[1.75] mb-7 ${i === 0 ? "drop-cap" : ""}`}
              >
                {p}
              </p>
            ))}

            <div className="rule my-16" />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="overline mb-3">Reserve · Direct or via Concierge</p>
                <p className="font-serif-ml text-2xl text-[var(--ml-text-dim)]">
                  Book online, or have us arrange it personally within 24 hours.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {listing.reservation_url && (
                  <a
                    href="https://booking.travelchannelworld.com/en/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="listing-detail-reserve"
                    className="btn-ml-gold inline-block whitespace-nowrap"
                  >
                    Reserve now
                  </a>
                )}
                <Link to="/concierge" data-testid="listing-detail-inquire" className="btn-ml-ghost inline-block whitespace-nowrap">
                  Concierge inquiry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {listing.gallery && listing.gallery.length > 0 && (
        <section className="container-ml pb-24 md:pb-32" data-testid="listing-gallery">
          <p className="overline mb-10">Gallery</p>
          <div className="grid grid-cols-12 gap-4 md:gap-8 auto-rows-[200px] md:auto-rows-[320px]">
            {listing.gallery.slice(0, 6).map((img, i) => {
              const span = i === 0 ? "col-span-12 md:col-span-7 row-span-2"
                : i === 1 ? "col-span-12 md:col-span-5 row-span-1"
                : i === 2 ? "col-span-6 md:col-span-5 row-span-1"
                : "col-span-6 md:col-span-4 row-span-1";
              return (
                <div key={i} className={`relative overflow-hidden border border-white/5 ${span}`}>
                  <img src={img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover moody-img" />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="container-ml pb-32" data-testid="listing-related">
          <div className="flex items-end justify-between mb-12 border-b border-white/10 pb-6">
            <h2 className="font-serif-ml text-4xl md:text-6xl tracking-tight leading-[0.95] text-[var(--ml-text)]">
              Further from <em className="text-[var(--ml-gold)]">{CATEGORY_LABELS[listing.category]}</em>
            </h2>
            <Link to={`/category/${listing.category}`} data-testid="related-see-all" className="hidden md:inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.25em] text-[var(--ml-gold)] link-line">
              See all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {related.map((r, i) => (
              <Link key={r.slug} to={`/listing/${r.slug}`} data-testid={`related-${r.slug}`} className="group block">
                <div className="aspect-[4/5] overflow-hidden border border-white/5 mb-5">
                  <img src={r.hero_image} alt={r.name} loading="lazy" className="w-full h-full object-cover moody-img" />
                </div>
                <p className="font-mono-ml text-[0.6rem] tracking-[0.3em] uppercase text-[var(--ml-text-muted)] mb-2">№ {String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-serif-ml text-2xl tracking-tight text-[var(--ml-text)] group-hover:text-[var(--ml-gold)] transition-colors">{r.name}</h3>
                <p className="text-sm text-[var(--ml-text-dim)] mt-1">{r.location}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
